import { z } from 'zod';
import { KI_AUFSCHLAG_PROZENT, kiPreisAus } from './anreicherung';
import type { KiAufgabe } from './kiAufgaben';

/**
 * Preise der KI von DXL: Einkauf beim Anbieter und eigener Verkaufspreis von DXL je Modell.
 *
 * Kein Anbieter (Anthropic, OpenAI, Google, Stability, Replicate) liefert Preise über seine API (geprüft am
 * Stand der Liste). Darum diese gepflegte Liste in US-Dollar mit Datum des Stands. DXL rechnet sie mit einem
 * festen Kurs in Euro-Cent um und kann den Einkauf je Modell von Hand überschreiben.
 *
 * Einheiten: Tokens in Cent je 1 Mio. Tokens (= Mikro-Cent je Token), Bilder in Cent je Bild.
 */

export interface KiListenpreis {
  anbieter: string;
  modell: string;
  /** USD je 1 Mio. Tokens */
  eingabeUsd: number;
  ausgabeUsd: number;
  /** USD je Bild (Standardgröße 1024×1024, mittlere Qualität) */
  bildUsd?: number;
  hinweis?: string;
}

/** Stand der Liste: Preisseiten der Anbieter, gelesen am Datum `stand` */
export const KI_PREISLISTE: { stand: string; quellen: Record<string, string>; preise: readonly KiListenpreis[] } = {
  stand: '2026-11-05',
  quellen: {
    anthropic: 'https://platform.claude.com/docs/en/about-claude/pricing',
    openai: 'https://developers.openai.com/api/docs/pricing',
    gemini: 'https://aistudio.google.com/docs/pricing',
    stability: 'https://docs.stability.ai/pricing',
    replicate: 'https://replicate.com/pricing',
  },
  preise: [
    // Anthropic
    { anbieter: 'anthropic', modell: 'claude-fable-5-1', eingabeUsd: 10, ausgabeUsd: 50 },
    { anbieter: 'anthropic', modell: 'claude-opus-5-5', eingabeUsd: 4, ausgabeUsd: 20 },
    { anbieter: 'anthropic', modell: 'claude-sonnet-5-5', eingabeUsd: 2, ausgabeUsd: 10 },
    { anbieter: 'anthropic', modell: 'claude-haiku-5-5', eingabeUsd: 0.1, ausgabeUsd: 0.5, hinweis: 'Prompt bis 100.000 Tokens; darüber 0,50 / 2,50 USD' },
    { anbieter: 'anthropic', modell: 'claude-fable-5', eingabeUsd: 10, ausgabeUsd: 50 },
    { anbieter: 'anthropic', modell: 'claude-opus-5', eingabeUsd: 5, ausgabeUsd: 25 },
    { anbieter: 'anthropic', modell: 'claude-sonnet-5', eingabeUsd: 2, ausgabeUsd: 10 },
    { anbieter: 'anthropic', modell: 'claude-opus-4-8', eingabeUsd: 5, ausgabeUsd: 25 },
    { anbieter: 'anthropic', modell: 'claude-opus-4-7', eingabeUsd: 5, ausgabeUsd: 25 },
    { anbieter: 'anthropic', modell: 'claude-opus-4-6', eingabeUsd: 5, ausgabeUsd: 25 },
    { anbieter: 'anthropic', modell: 'claude-sonnet-4-6', eingabeUsd: 3, ausgabeUsd: 15 },
    { anbieter: 'anthropic', modell: 'claude-sonnet-4-5', eingabeUsd: 3, ausgabeUsd: 15 },
    { anbieter: 'anthropic', modell: 'claude-haiku-4-5', eingabeUsd: 1, ausgabeUsd: 5 },
    // OpenAI – Text
    { anbieter: 'openai', modell: 'gpt-6-astra', eingabeUsd: 10, ausgabeUsd: 50 },
    { anbieter: 'openai', modell: 'gpt-6.1-sol', eingabeUsd: 2, ausgabeUsd: 10 },
    { anbieter: 'openai', modell: 'gpt-6.1-terra', eingabeUsd: 1, ausgabeUsd: 5 },
    { anbieter: 'openai', modell: 'gpt-6-sol', eingabeUsd: 2, ausgabeUsd: 10 },
    { anbieter: 'openai', modell: 'gpt-6-luna', eingabeUsd: 0.1, ausgabeUsd: 0.5 },
    { anbieter: 'openai', modell: 'gpt-5.6-sol', eingabeUsd: 4, ausgabeUsd: 20, hinweis: 'Aktionspreis bis 30.11.2026' },
    { anbieter: 'openai', modell: 'gpt-5.6-terra', eingabeUsd: 2, ausgabeUsd: 12 },
    { anbieter: 'openai', modell: 'gpt-5.6-luna', eingabeUsd: 0.2, ausgabeUsd: 1.2 },
    { anbieter: 'openai', modell: 'gpt-5.5', eingabeUsd: 5, ausgabeUsd: 30 },
    { anbieter: 'openai', modell: 'gpt-5.4', eingabeUsd: 2.5, ausgabeUsd: 15 },
    { anbieter: 'openai', modell: 'gpt-5.4-mini', eingabeUsd: 0.75, ausgabeUsd: 4.5 },
    { anbieter: 'openai', modell: 'gpt-5.4-nano', eingabeUsd: 0.2, ausgabeUsd: 1.25 },
    { anbieter: 'openai', modell: 'gpt-5.2', eingabeUsd: 1.75, ausgabeUsd: 14 },
    { anbieter: 'openai', modell: 'gpt-5.1', eingabeUsd: 1.25, ausgabeUsd: 10 },
    { anbieter: 'openai', modell: 'gpt-5', eingabeUsd: 1.25, ausgabeUsd: 10 },
    { anbieter: 'openai', modell: 'gpt-5-mini', eingabeUsd: 0.25, ausgabeUsd: 2 },
    { anbieter: 'openai', modell: 'gpt-5-nano', eingabeUsd: 0.05, ausgabeUsd: 0.4 },
    { anbieter: 'openai', modell: 'gpt-4.1', eingabeUsd: 2, ausgabeUsd: 8 },
    { anbieter: 'openai', modell: 'gpt-4.1-mini', eingabeUsd: 0.4, ausgabeUsd: 1.6 },
    { anbieter: 'openai', modell: 'gpt-4o', eingabeUsd: 2.5, ausgabeUsd: 10 },
    { anbieter: 'openai', modell: 'gpt-4o-mini', eingabeUsd: 0.15, ausgabeUsd: 0.6 },
    { anbieter: 'openai', modell: 'o3', eingabeUsd: 2, ausgabeUsd: 8 },
    // OpenAI – Bild (je Bild 1024×1024, Qualität medium)
    { anbieter: 'openai', modell: 'gpt-image-2', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.053, hinweis: 'low 0,006 / high 0,211 USD je Bild' },
    { anbieter: 'openai', modell: 'gpt-image-1.5', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.034, hinweis: 'low 0,009 / high 0,133 USD je Bild' },
    { anbieter: 'openai', modell: 'gpt-image-1-mini', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.011, hinweis: 'low 0,005 / high 0,036 USD je Bild' },
    // Google Gemini – Text
    { anbieter: 'gemini', modell: 'gemini-4-argon', eingabeUsd: 2, ausgabeUsd: 10, hinweis: 'ab 01.12.2026: 4 / 20 USD' },
    { anbieter: 'gemini', modell: 'gemini-3.8-flash', eingabeUsd: 0.75, ausgabeUsd: 3.75, hinweis: 'ab 01.01.2027: 1,50 / 7,50 USD' },
    { anbieter: 'gemini', modell: 'gemini-3.6-flash', eingabeUsd: 0.75, ausgabeUsd: 3.75, hinweis: 'ab 01.01.2027: 1,50 / 7,50 USD' },
    { anbieter: 'gemini', modell: 'gemini-3.5-flash-lite', eingabeUsd: 0.3, ausgabeUsd: 2.5 },
    { anbieter: 'gemini', modell: 'gemini-3.1-pro-preview', eingabeUsd: 2, ausgabeUsd: 12, hinweis: 'Prompt bis 200.000 Tokens' },
    { anbieter: 'gemini', modell: 'gemini-3.1-flash-lite', eingabeUsd: 0.25, ausgabeUsd: 1.5 },
    { anbieter: 'gemini', modell: 'gemini-3-flash-preview', eingabeUsd: 0.5, ausgabeUsd: 3 },
    { anbieter: 'gemini', modell: 'gemini-2.5-pro', eingabeUsd: 1.25, ausgabeUsd: 10, hinweis: 'abgekündigt – ab 01.12.2026 auf gemini-3.8-flash umgeleitet' },
    { anbieter: 'gemini', modell: 'gemini-2.5-flash', eingabeUsd: 0.3, ausgabeUsd: 2.5, hinweis: 'abgekündigt – ab 01.12.2026 auf gemini-3.5-flash-lite umgeleitet' },
    { anbieter: 'gemini', modell: 'gemini-2.5-flash-lite', eingabeUsd: 0.1, ausgabeUsd: 0.4 },
    // Google Gemini – Bild (je Bild in Standardgröße 1K)
    { anbieter: 'gemini', modell: 'gemini-3.1-flash-image', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.067 },
    { anbieter: 'gemini', modell: 'gemini-3.1-flash-lite-image', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.0336 },
    { anbieter: 'gemini', modell: 'gemini-3-pro-image', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.134 },
    { anbieter: 'gemini', modell: 'gemini-2.5-flash-image', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.039, hinweis: 'abgekündigt, Abschaltung 15.03.2027' },
    // Stability AI – Dienste je Bild (1 Credit = 0,01 USD)
    { anbieter: 'stability', modell: 'remove-background', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.05 },
    { anbieter: 'stability', modell: 'replace-background-and-relight', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.08 },
    { anbieter: 'stability', modell: 'stable-image-core', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.03 },
    { anbieter: 'stability', modell: 'stable-image-ultra', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.08 },
    { anbieter: 'stability', modell: 'fast-upscaler', eingabeUsd: 0, ausgabeUsd: 0, bildUsd: 0.02 },
  ],
};

/** Datums- oder Versionsanhang einer Modell-ID (claude-…-20250929, gpt-4.1-2025-04-14, gemini-…-001, -latest) */
const ANHANG = /^-(\d{8}|\d{4}-\d{2}-\d{2}|\d{3}|latest)$/;

/** Listenpreis zu einer Modell-ID – genau oder mit Datums-/Versionsanhang, sonst keiner */
export function kiListenpreis(anbieter: string, modell: string, liste: readonly KiListenpreis[] = KI_PREISLISTE.preise): KiListenpreis | null {
  let treffer: KiListenpreis | null = null;
  for (const p of liste) {
    if (anbieter && p.anbieter !== anbieter) continue;
    if (p.modell === modell) return p;
    if (modell.startsWith(p.modell) && ANHANG.test(modell.slice(p.modell.length)) && (!treffer || p.modell.length > treffer.modell.length)) treffer = p;
  }
  return treffer;
}

/** Cent-Werte mit vier Nachkommastellen (wie numeric(14, 4)) */
export const kiCentRunden = (c: number) => Math.round(c * 10_000) / 10_000;

/** Listenpreis in Euro-Cent zum festen Kurs */
export function kiListeInCent(p: KiListenpreis, usdEur: number): KiEinkauf {
  return {
    eingabe: kiCentRunden(p.eingabeUsd * usdEur * 100),
    ausgabe: kiCentRunden(p.ausgabeUsd * usdEur * 100),
    bild: p.bildUsd !== undefined ? kiCentRunden(p.bildUsd * usdEur * 100) : null,
  };
}

/** Einkauf: Cent je 1 Mio. Tokens, Bild in Cent je Bild (null = Modell rechnet nicht je Bild ab) */
export interface KiEinkauf {
  eingabe: number;
  ausgabe: number;
  bild: number | null;
}
/** Verkaufspreis von DXL – null = nicht festgelegt (Übergang: Einkauf × 1,1) */
export interface KiVerkauf {
  eingabe: number | null;
  ausgabe: number | null;
  bild: number | null;
}

export type KiPreisRegel = 'verkauf' | 'aufschlag';
export type KiEinkaufQuelle = 'hand' | 'liste';

/** Wirksamer Preis eines Modells: Einkauf und Preis für den Marktplatz (beides in Cent je Mio. / je Bild) */
export interface KiPreisWirksam {
  einkauf: KiEinkauf;
  einkaufQuelle: KiEinkaufQuelle;
  /** Preis für den Marktplatz; ohne Verkaufspreis Einkauf × 1,1 */
  marktplatz: { eingabe: number; ausgabe: number; bild: number | null };
  /** Tokens bzw. Bild: eigener Verkaufspreis gesetzt? */
  verkaufTokens: boolean;
  verkaufBild: boolean;
  /** „verkauf“, wenn alles, was das Modell kostet, einen Verkaufspreis hat */
  regel: KiPreisRegel;
}

const aufschlag = (c: number) => kiCentRunden((c * (100 + KI_AUFSCHLAG_PROZENT)) / 100);

/**
 * Preis eines Modells aus der gespeicherten Zeile (ki_preis) oder, ohne Zeile, aus der Liste zum Kurs.
 * null = kein Einkaufspreis bekannt → Modell für die KI von DXL nicht nutzbar.
 */
export function kiPreisWirksam(
  zeile: { einkauf: KiEinkauf; einkaufQuelle: KiEinkaufQuelle; verkauf: KiVerkauf } | null,
  liste: KiListenpreis | null,
  usdEur: number | null,
): KiPreisWirksam | null {
  const einkauf = zeile?.einkauf ?? (liste && usdEur ? kiListeInCent(liste, usdEur) : null);
  if (!einkauf) return null;
  const v = zeile?.verkauf ?? { eingabe: null, ausgabe: null, bild: null };
  const verkaufTokens = v.eingabe !== null && v.ausgabe !== null;
  const verkaufBild = v.bild !== null;
  const tokensKosten = einkauf.eingabe > 0 || einkauf.ausgabe > 0;
  const bildKosten = einkauf.bild !== null;
  return {
    einkauf,
    einkaufQuelle: zeile?.einkaufQuelle ?? 'liste',
    marktplatz: {
      eingabe: verkaufTokens ? v.eingabe! : aufschlag(einkauf.eingabe),
      ausgabe: verkaufTokens ? v.ausgabe! : aufschlag(einkauf.ausgabe),
      bild: verkaufBild ? v.bild : einkauf.bild !== null ? aufschlag(einkauf.bild) : null,
    },
    verkaufTokens,
    verkaufBild,
    regel: (verkaufTokens || !tokensKosten) && (verkaufBild || !bildKosten) && (verkaufTokens || verkaufBild) ? 'verkauf' : 'aufschlag',
  };
}

export interface KiNutzungMenge {
  eingabe: number;
  ausgabe: number;
  bilder?: number;
}

/**
 * Kosten einer Nutzung in Mikro-Cent: Einkauf und Preis für den Marktplatz.
 * Ohne Verkaufspreis bleibt es bei der bisherigen Rechnung (Einkauf × 1,1 auf den Betrag), mit Verkaufspreis
 * zählt der Verkaufspreis.
 */
export function kiKosten(n: KiNutzungMenge, p: KiPreisWirksam): { einkauf: number; preis: number } {
  const bilder = n.bilder ?? 0;
  const tokEinkauf = Math.round(n.eingabe * p.einkauf.eingabe + n.ausgabe * p.einkauf.ausgabe);
  const bildEinkauf = Math.round(bilder * (p.einkauf.bild ?? 0) * 1_000_000);
  const tokPreis = p.verkaufTokens ? Math.round(n.eingabe * p.marktplatz.eingabe + n.ausgabe * p.marktplatz.ausgabe) : kiPreisAus(tokEinkauf);
  const bildPreis = p.verkaufBild ? Math.round(bilder * (p.marktplatz.bild ?? 0) * 1_000_000) : kiPreisAus(bildEinkauf);
  return { einkauf: tokEinkauf + bildEinkauf, preis: tokPreis + bildPreis };
}

/** Spanne zwischen Einkauf und Verkauf: Betrag und Anteil am Verkaufspreis (Handelsspanne) */
export function kiSpanne(einkauf: number, verkauf: number): { cent: number; prozent: number | null } {
  return { cent: kiCentRunden(verkauf - einkauf), prozent: verkauf > 0 ? Math.round(((verkauf - einkauf) / verkauf) * 1000) / 10 : null };
}

/* ------------------------------------------------------------ Schnittstelle Plattform-Admin */

/** Zeile der Preistabelle: alle Modelle der verbundenen Anbieter, gespeicherte Preise und zugeordnete Modelle */
export interface KiPreisAnsicht {
  anbieter: string;
  modell: string;
  art: 'text' | 'bild' | null;
  /** in der Modellliste eines verbundenen Anbieters */
  verbunden: boolean;
  /** in ki_preis gespeichert */
  gespeichert: boolean;
  /** Aufgaben der KI von DXL, die das Modell nutzen (auch als Ersatz) */
  aufgaben: KiAufgabe[];
  einkauf: KiEinkauf | null;
  einkaufQuelle: KiEinkaufQuelle | null;
  einkaufStand: string | null;
  /** aktueller Listenpreis zum Kurs; null = nicht in der Liste */
  liste: (KiEinkauf & { hinweis: string | null }) | null;
  /** gespeicherter Einkauf aus der Liste weicht vom aktuellen Listenpreis ab */
  listeGeaendert: boolean;
  verkauf: KiVerkauf;
  wirksam: KiPreisWirksam | null;
  geaendertAm: string | null;
}

export interface KiPreisUebersicht {
  kurs: { usdEur: number | null; stand: string | null };
  liste: { stand: string; quellen: Record<string, string> };
  modelle: KiPreisAnsicht[];
}

const Cent = z.coerce.number().min(0).max(10_000_000);
/** null zuerst prüfen – z.coerce.number() machte aus null sonst 0 (= kostenlos) */
const CentOptional = z.union([z.null(), Cent]).default(null);

export const KiPreisSetzenSchema = z
  .object({
    anbieter: z.string().trim().max(40).default(''),
    modell: z.string().trim().min(1, 'Bitte die Modell-ID angeben').max(160),
    /** „liste“ = Einkauf aus der Preisliste zum Kurs; sonst von Hand */
    einkauf: z.union([z.literal('liste'), z.object({ eingabe: Cent, ausgabe: Cent, bild: CentOptional })]),
    verkauf: z.object({ eingabe: CentOptional, ausgabe: CentOptional, bild: CentOptional }),
  })
  .superRefine((d, ctx) => {
    if ((d.verkauf.eingabe === null) !== (d.verkauf.ausgabe === null)) {
      ctx.addIssue({ code: 'custom', path: ['verkauf', d.verkauf.eingabe === null ? 'eingabe' : 'ausgabe'], message: 'Bitte Eingabe und Ausgabe zusammen angeben' });
    }
  });
export type KiPreisSetzenEingabe = z.input<typeof KiPreisSetzenSchema>;

export const KiKursSchema = z.object({ usdEur: z.coerce.number().gt(0).max(10) });

/** Verbrauch eines Marktplatzes nach Aufgabe und Modell */
export interface KiVerbrauchZeile {
  aufgabe: KiAufgabe;
  anbieter: string;
  modell: string;
  aufrufe: number;
  tokens: number;
  bilder: number;
  einkaufCent: number;
  verkaufCent: number;
  margeCent: number;
  /** Anteil der Aufrufe mit Ersatzmodell */
  ersatz: number;
}

/** Preise für den Marktplatz je Aufgabe (Einstellungen → KI bei „KI von DXL“) */
export interface KiAufgabePreis {
  aufgabe: KiAufgabe;
  anbieter: string;
  modell: string;
  ausStandard: boolean;
  /** Cent je 1 Mio. Tokens bzw. je Bild; null = Aufgabe bei DXL nicht verfügbar */
  preis: { eingabeCentJeMio: number; ausgabeCentJeMio: number; bildCent: number | null } | null;
}
